import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { DataTokenPage } from "./components/DataTokenPage";
import { ExcelQueryPage } from "./components/ExcelQueryPage";
import { isDataTokenPage, isExcelQueryPage } from "./utils/webPages";
import { bootstrapTigerResources } from "./utils/tigerResource";
import { installContextMenuSuppressor } from "./utils/contextMenuSuppressor";
import { installLiveTabFetch } from "./utils/liveTabFetch";
import { installBackendRestartWatch } from "./utils/backendRestartWatch";
import { getRenderWorkerToken } from "./utils/renderWorkerMode";
import { installUiTheme } from "./theme/uiTheme";
import "./styles/global.css";
import "./styles/TigerStyleSheet.css";

// Apply the persisted UI chrome theme before anything renders.
installUiTheme();

const renderWorkerToken =
  typeof window !== "undefined" ? getRenderWorkerToken(window.location.search) : null;

if (renderWorkerToken) {
  // Headless render-worker mode: authenticate as the service principal (the
  // worker browser has no login) and drive report render jobs. Never mount the
  // normal UI. The command-line token is exchanged server-side for an HttpOnly
  // session cookie (so page script can never read it), then dropped from the
  // URL so it doesn't linger in history or referrers. Resources are still
  // bootstrapped so graph legends resolve their localized headers
  // (getTigerResource).
  void (async () => {
    try {
      await fetch("/api/auth/worker-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: renderWorkerToken }),
      });
    } catch {
      // If this fails the worker's fetches 401 (visible: blank report graphs)
      // rather than falling back to a script-readable token.
    }
    try {
      window.history.replaceState({}, "", window.location.pathname);
    } catch {
      /* replaceState unavailable; non-fatal */
    }
    await bootstrapTigerResources().catch(() => {});
    const { runRenderWorker } = await import("./utils/reportGraphRenderer");
    void runRenderWorker();
  })();
} else {
  // Install before any fetch fires so a web ?live= tab stamps X-Tiger-Tab on every
  // /api/* request (own backend session + turbine-scoped dialogs).
  installLiveTabFetch();
  // Reload (reconnect or fall back to the login screen) when the webserver
  // restarts under a still-open page. Not installed in render-worker mode:
  // the worker is spawned per job by the backend itself.
  installBackendRestartWatch();
  installContextMenuSuppressor();
  // /authtoken (access tokens) and /exceldata (Excel query builder) are their
  // own pages in the same shell.
  const path = window.location.pathname;
  const page = isDataTokenPage(path) ? <DataTokenPage /> : isExcelQueryPage(path) ? <ExcelQueryPage /> : <App />;
  void bootstrapTigerResources().finally(() => {
    ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
      <React.StrictMode>{page}</React.StrictMode>,
    );
  });
}
